import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaArrowLeft, FaChevronDown, FaChevronRight, FaCopy, FaEdit, FaFileContract, FaFilePdf, FaListUl, FaPlus, FaSave, FaSearch, FaTimes, FaTrash, FaEraser, FaInfoCircle } from 'react-icons/fa';
import api from "../../api";
import { generateDocumentPdf, generateDocumentPdfBatch } from "../../services/documentPdfService";
import {
  buildAttendeeDocumentVariables,
  groupAttendeeDocumentVariables,
} from "./attendeeDocumentVariables";
import { sanitizeDocumentTemplateHtml } from "../../security/documentTemplateHtml";
import DocumentTemplateAiAssistant from "../../components/DocumentTemplateAiAssistant";
import { clearDocumentTemplateDraft } from "../documentTemplateEditorUtils";
import { findSavedTemplateInList, templateFromSaveResponse, templateHasId } from "../documentTemplateSaveResponse";
import { exactRenderEndpoint, exactTemplateSummary, isExactPdfTemplate, makeExactTemplateEditable } from "../documentTemplateExactMode";
import "./attendeeDocuments.css";

const ATTENDEE_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Ativos" },
  { value: "IN_PROCESS", label: "Em processo" },
  { value: "INACTIVE", label: "Inativos" },
  { value: "ALL", label: "Todos" },
];

const defaultTemplate = () => ({
  id: null,
  name: "Declaração de matrícula",
  title: "Declaração de Matrícula — {{nome_completo}}",
  content:
    "<p>Declaramos, para os devidos fins, que <strong>{{nome_completo}}</strong>, CPF {{cpf}}, encontra-se matriculado(a) sob o número {{numero_matricula}}, na turma {{turma}}, grupo {{grupo}}.</p><p>Por ser verdade, firmamos a presente declaração.</p><p>{{data_atual_extenso}}.</p>",
  active: true,
  sortOrder: 0,
  templateMode: "html",
  sourcePdfRef: null,
  overlayConfig: [],
});

const cleanTemplateHtml = (value) =>
  sanitizeDocumentTemplateHtml(value)
    .replace(/<img\\b[^>]*>/gi, "")
    .replace(/\\{\\{\\s*calc:[^}]+\\}\\}/gi, "");

const messageFrom = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("pt-BR");

const normalizeEnrollmentDeclaration = (template) => {
  if (!template || normalizeText(template.name) !== "declaracao de matricula") {
    return template;
  }
  return {
    ...template,
    content: String(template.content || "")
      .replace(/\{\{\s*cidade\s*\}\}\s*,?\s*/gi, "")
      .replace(/\{\{\s*curso\s*\}\}/gi, "{{grupo}}")
      .replace(/\bcurso\b/gi, (word) => word[0] === "C" ? "Grupo" : "grupo"),
  };
};

const safeFileNamePart = (value, fallback) =>
  String(value || fallback || "documento")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || fallback || "documento";

const attendeeStatus = (attendee) => String(attendee?.status || "ACTIVE");

const sortAttendeesByName = (items = []) =>
  [...items].sort((left, right) =>
    String(left?.fullName || "").localeCompare(String(right?.fullName || ""), "pt-BR", {
      sensitivity: "base",
      numeric: true,
    }),
  );

function VariableBrowser({ fields, onInsert }) {
  const [query, setQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const grouped = useMemo(() => groupAttendeeDocumentVariables(fields), [fields]);
  const normalizedQuery = normalizeText(query);
  const searching = Boolean(normalizedQuery);

  const toggleGroup = (group) => {
    setExpandedGroups((current) => ({
      ...current,
      [group]: !current[group],
    }));
  };

  return (
    <aside className="attendee-docs__variables">
      <label className="attendee-docs__search">
        <FaSearch />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar variável"
        />
      </label>
      <div className="attendee-docs__variable-scroll">
        {Object.entries(grouped).map(([group, items]) => {
          const visible = items.filter(
            (field) =>
              !normalizedQuery ||
              normalizeText(field.label).includes(normalizedQuery) ||
              normalizeText(field.id).includes(normalizedQuery),
          );
          if (!visible.length) return null;
          const expanded = searching || Boolean(expandedGroups[group]);
          return (
            <section key={group}>
              <button
                type="button"
                className="attendee-docs__variable-group-toggle"
                onClick={() => toggleGroup(group)}
                aria-expanded={expanded}
              >
                <strong>{group}</strong>
                <span>
                  {visible.length}
                  {expanded ? <FaChevronDown /> : <FaChevronRight />}
                </span>
              </button>
              {expanded && (
                <div className="attendee-docs__variable-group-items">
                  {visible.map((field) => {
                    const token = `{{${field.id}}}`;
                    return (
                      <button
                        key={field.id}
                        type="button"
                        className="attendee-docs__variable-item"
                        title={`Inserir ${token}`}
                        onClick={() => onInsert(token)}
                      >
                        <span>{field.label}</span>
                        <code>{token}</code>
                        <FaCopy />
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </aside>
  );
}

function TemplateEditor({ value, onChange, onSave, saving, variables }) {
  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const exactSummary = exactTemplateSummary(value);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = cleanTemplateHtml(value.content || "");
    }
    // O conteúdo muda a cada tecla; sincronizá-lo aqui reposicionaria o cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.id, value._sourceRevision]);

  const updateContent = () => {
    const content = cleanTemplateHtml(editorRef.current?.innerHTML || "");
    onChange(
      isExactPdfTemplate(value)
        ? makeExactTemplateEditable(value, content)
        : { ...value, content },
    );
  };

  const handlePaste = (event) => {
    const clipboardHtml = event.clipboardData?.getData("text/html") || "";
    if (!clipboardHtml) return;
    event.preventDefault();
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, cleanTemplateHtml(clipboardHtml));
    updateContent();
  };

  const command = (name, argument = null) => {
    editorRef.current?.focus();
    document.execCommand(name, false, argument);
    updateContent();
  };

  const applyTextColor = (color) => {
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("foreColor", false, color);
    updateContent();
  };

  const adjustFontSize = (delta) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;

    const anchor =
      range.startContainer.nodeType === Node.ELEMENT_NODE
        ? range.startContainer
        : range.startContainer.parentElement;
    const currentSize = Number.parseFloat(
      window.getComputedStyle(anchor || editor).fontSize,
    ) || 16;
    const nextSize = Math.min(48, Math.max(8, currentSize + delta));

    editor.focus();
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, "7");
    editor.querySelectorAll('font[size="7"]').forEach((element) => {
      element.removeAttribute("size");
      element.style.fontSize = `${nextSize}px`;
    });
    updateContent();
  };

  const keepEditorSelection = (event) => event.preventDefault();

  const captureEditorSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) {
      selectionRef.current = range.cloneRange();
    }
  };

  const applyAlignment = (name) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (selectionRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(selectionRef.current);
    }
    document.execCommand(name, false, null);
    captureEditorSelection();
    updateContent();
  };

  const insertVariable = (token) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    const saved = selectionRef.current;
    if (selection && saved && editor.contains(saved.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(saved);
    }
    document.execCommand("insertText", false, token);
    captureEditorSelection();
    updateContent();
  };

  const saveCurrentTemplate = () => {
    const editorHtml = editorRef.current?.innerHTML ?? value.content ?? "";
    const content = cleanTemplateHtml(editorHtml);
    const nextValue = isExactPdfTemplate(value)
      ? makeExactTemplateEditable(value, content)
      : { ...value, content };
    onChange(nextValue);
    onSave(nextValue);
  };

  const clearFields = () => {
    if (!window.confirm("Limpar todos os campos deste modelo? Nome, título, conteúdo, ordem e configurações atuais serão apagados do formulário.")) {
      return;
    }
    const cleared = clearDocumentTemplateDraft(value);
    onChange(cleared);
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  return (
    <div className="attendee-docs__editor-layout">
      <div className="attendee-docs__editor">
        <div className="attendee-docs__editor-top-actions">
          <button type="button" className="attendee-docs__clear-fields" onClick={clearFields}>
            <FaEraser /> Limpar
          </button>
          <button type="button" disabled={saving} onClick={saveCurrentTemplate}>
            <FaSave /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
        <div data-document-template-ai="attendee">
          <DocumentTemplateAiAssistant
            target="attendee"
            template={value}
            variables={variables}
            onApply={(generated) => {
              const next = {
                ...value,
                ...generated,
                content: cleanTemplateHtml(generated?.content || ""),
              };
              onChange(next);
              if (editorRef.current) editorRef.current.innerHTML = next.content;
            }}
          />
        </div>
        <div className="attendee-docs__form-row">
          <label>
            Nome do modelo
            <input
              maxLength={180}
              value={value.name || ""}
              onChange={(event) => onChange({ ...value, name: event.target.value })}
            />
          </label>
          <label>
            Ordem
            <input
              type="number"
              min="0"
              max="10000"
              value={value.sortOrder || 0}
              onChange={(event) =>
                onChange({ ...value, sortOrder: Number(event.target.value || 0) })
              }
            />
          </label>
          <label className="attendee-docs__toggle">
            <input
              type="checkbox"
              checked={value.active !== false}
              onChange={(event) =>
                onChange({ ...value, active: event.target.checked })
              }
            />
            Modelo ativo
          </label>
        </div>
        <label>
          Título do documento
          <input
            maxLength={500}
            value={value.title || ""}
            onChange={(event) => onChange({ ...value, title: event.target.value })}
            placeholder="Contrato de matrícula — {{nome_completo}}"
          />
        </label>
        {exactSummary.exact && (
          <div className="attendee-docs__exact-template">
            <span className="attendee-docs__exact-template-icon"><FaFilePdf /></span>
            <div>
              <strong>PDF original preservado</strong>
              <p>Este é um modelo antigo baseado no PDF original. O editor abaixo está liberado; ao alterar qualquer conteúdo, ele passa automaticamente para a versão editável.</p>
              <div className="attendee-docs__exact-template-meta">
                <span>{value.sourceFileName || "PDF anexado"}</span>
                <span>{exactSummary.mappedVariables} variável(is) mapeada(s)</span>
              </div>
              <small>Para obter o texto inteiro já preenchido e formatado, envie novamente o PDF na Nash IA; os novos modelos entram diretamente no editor.</small>
            </div>
          </div>
        )}

          <div className="attendee-docs__toolbar" aria-label="Formatação do documento">
            <button type="button" title="Negrito" onClick={() => command("bold")}>
              <strong>B</strong>
            </button>
            <button type="button" title="Itálico" onClick={() => command("italic")}>
              <em>I</em>
            </button>
            <button
              type="button"
              title="Sublinhado"
              onClick={() => command("underline")}
            >
              <u>U</u>
            </button>
            <label className="attendee-docs__color-tool" title="Cor do texto">
              <span>Cor</span>
              <input
                type="color"
                defaultValue="#111827"
                aria-label="Cor do texto"
                onChange={(event) => applyTextColor(event.target.value)}
              />
            </label>
            <select
              className="attendee-docs__alignment-tool"
              aria-label="Alinhamento do texto"
              title="Alinhamento do texto"
              defaultValue="justifyLeft"
              onMouseDown={captureEditorSelection}
              onFocus={captureEditorSelection}
              onChange={(event) => applyAlignment(event.target.value)}
            >
              <option value="justifyLeft">Esquerda</option>
              <option value="justifyCenter">Centro</option>
              <option value="justifyRight">Direita</option>
              <option value="justifyFull">Justificar</option>
            </select>
            <button
              type="button"
              title="Diminuir tamanho da fonte"
              aria-label="Diminuir tamanho da fonte"
              onMouseDown={keepEditorSelection}
              onClick={() => adjustFontSize(-2)}
            >
              A−
            </button>
            <button
              type="button"
              title="Aumentar tamanho da fonte"
              aria-label="Aumentar tamanho da fonte"
              onMouseDown={keepEditorSelection}
              onClick={() => adjustFontSize(2)}
            >
              A+
            </button>
            <button
              type="button"
              title="Lista com marcadores"
              onClick={() => command("insertUnorderedList")}
            >
              <FaListUl /> Lista
            </button>
          </div>
          <div
            ref={editorRef}
            className="attendee-docs__content-editor"
            contentEditable
            suppressContentEditableWarning
            onInput={updateContent}
            onPaste={handlePaste}
            onMouseUp={captureEditorSelection}
            onKeyUp={captureEditorSelection}
            onBlur={captureEditorSelection}
            data-placeholder="Digite o conteúdo do contrato, matrícula, declaração ou autorização..."
          />
          <small>
            O conteúdo aceita formatação, listas, tabelas e variáveis no formato{" "}
            <code>{"{{nome_completo}}"}</code>.
          </small>
  


      </div>
      <div className="attendee-docs__variables-mobile">
        <VariableBrowser fields={variables} onInsert={insertVariable} />
      </div>
      <div className="attendee-docs__variables-desktop">
        <VariableBrowser fields={variables} onInsert={insertVariable} />
      </div>
    </div>
  );
}

export default function AttendeeDocumentWorkspace() {
  const diaryRequest = useRef(0);
  useEffect(() => () => { diaryRequest.current += 1; }, []);

  const [visible, setVisible] = useState(false);
  const [navigationSlot, setNavigationSlot] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [customAttendeeTabs, setCustomAttendeeTabs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [selectedAttendeeId, setSelectedAttendeeId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [templateQuery, setTemplateQuery] = useState("");
  const [attendeeQuery, setAttendeeQuery] = useState("");
  const [attendeeStatusFilter, setAttendeeStatusFilter] = useState("ACTIVE");
  const [tab, setTab] = useState("generate");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedAttendeeLoading, setSelectedAttendeeLoading] = useState(false);
  const [loadedAttendeeId, setLoadedAttendeeId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const attendeeDetailCacheRef = useRef(new Map());
  const attendanceCacheRef = useRef(new Map());

  useEffect(() => {
    let mountedSlot = null;
    const sync = () => {
      const attendeesRoot = document.querySelector(".attendees[data-diary-documents='true']");
      setVisible(Boolean(attendeesRoot));
      const navigation = attendeesRoot?.querySelector(".attendees__tabs");
      if (!navigation) {
        setNavigationSlot(null);
        return;
      }

      let slot = navigation.querySelector("[data-attendee-documents-slot]");
      if (!slot) {
        slot = document.createElement("span");
        slot.className = "attendee-docs__nav-slot";
        slot.setAttribute("data-attendee-documents-slot", "true");
        const buttons = Array.from(navigation.querySelectorAll(":scope > button"));
        const settingsButton = buttons.find(
          (button) => button.textContent?.trim() === "Configurações",
        );
        if (settingsButton) navigation.insertBefore(slot, settingsButton);
        else navigation.appendChild(slot);
      }
      mountedSlot = slot;
      setNavigationSlot(slot);
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-diary-documents"] });
    return () => {
      observer.disconnect();
      mountedSlot?.remove();
    };
  }, []);

  const load = async () => {
    setLoading(true);
    setError("");
    setAttendance([]);
    setLoadedAttendeeId("");
    attendeeDetailCacheRef.current.clear();
    attendanceCacheRef.current.clear();
    try {
      const [fastDataResponse, templatesResponse] = await Promise.all([
        api.get("/attendee-management/document-data-fast", { params: { status: "ACTIVE" } }),
        api.get("/attendee-management/document-templates"),
      ]);
      const nextAttendees = sortAttendeesByName(Array.isArray(fastDataResponse.data?.records) ? fastDataResponse.data.records : []);
      const nextTemplates = Array.isArray(templatesResponse.data?.templates)
        ? templatesResponse.data.templates.map(normalizeEnrollmentDeclaration)
        : [];
      setAttendees(nextAttendees);
      setTemplates(nextTemplates);
      setClasses(Array.isArray(fastDataResponse.data?.classes) ? fastDataResponse.data.classes : []);
      setGroups(Array.isArray(fastDataResponse.data?.groups) ? fastDataResponse.data.groups : []);
      const documentSettings = fastDataResponse.data?.settings || {};
      setCustomAttendeeTabs(Array.isArray(documentSettings.customAttendeeTabs) ? documentSettings.customAttendeeTabs : []);
      setCanManage(Boolean(templatesResponse.data?.canManage));
      setAttendeeStatusFilter("ACTIVE");
      setSelectedAttendeeId("");
      const firstTemplateId = String(nextTemplates.find((item) => item.active !== false)?.id || "");
      setSelectedTemplateId((current) => current && nextTemplates.some((item) => String(item.id) === String(current)) ? current : firstTemplateId);
      setSelectedTemplateIds((current) => {
        const available = current.filter((id) => nextTemplates.some((item) => String(item.id) === String(id) && item.active !== false));
        return available.length ? available : (firstTemplateId ? [firstTemplateId] : []);
      });
    } catch (loadError) {
      setError(messageFrom(loadError, "Não foi possível carregar os modelos de documentos dos atendidos."));
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async (nextStatus) => {
    setAttendeeStatusFilter(nextStatus);
    setLoading(true);
    setError("");
    setAttendance([]);
    setLoadedAttendeeId("");
    attendeeDetailCacheRef.current.clear();
    attendanceCacheRef.current.clear();
    try {
      const response = await api.get("/attendee-management/document-data-fast", { params: { status: nextStatus } });
      const rows = sortAttendeesByName(Array.isArray(response.data?.records) ? response.data.records : []);
      setAttendees(rows);
      setClasses(Array.isArray(response.data?.classes) ? response.data.classes : []);
      setGroups(Array.isArray(response.data?.groups) ? response.data.groups : []);
      setSelectedAttendeeId("");
    } catch (loadError) {
      setError(messageFrom(loadError, "Não foi possível carregar atendidos para o filtro selecionado."));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  useEffect(() => {
    if (!visible) { diaryRequest.current += 1; setOpen(false); setAttendees([]); setTemplates([]); setLoading(false); }
  }, [visible]);

  const filteredAttendees = useMemo(() => {
    const query = normalizeText(attendeeQuery);
    return sortAttendeesByName(
      attendees.filter((attendee) => {
        const matchesStatus =
          attendeeStatusFilter === "ALL" ||
          attendeeStatus(attendee) === attendeeStatusFilter;
        const matchesQuery =
          !query ||
          [
            attendee.fullName,
            attendee.socialName,
            attendee.registrationNumber,
            attendee.cpf,
          ].some((value) => normalizeText(value).includes(query));
        return matchesStatus && matchesQuery;
      }),
    );
  }, [attendeeQuery, attendeeStatusFilter, attendees]);

  useEffect(() => {
    if (!open || loading) return;
    const selectedIsVisible = filteredAttendees.some(
      (attendee) => String(attendee.id) === String(selectedAttendeeId),
    );
    if (!selectedIsVisible) {
      setSelectedAttendeeId(String(filteredAttendees[0]?.id || ""));
    }
  }, [filteredAttendees, loading, open, selectedAttendeeId]);

  useEffect(() => {
    if (!open || loading || !selectedAttendeeId) {
      if (!selectedAttendeeId) {
        setAttendance([]);
        setLoadedAttendeeId("");
      }
      return undefined;
    }

    let cancelled = false;
    const attendeeId = Number(selectedAttendeeId);
    const cachedAttendee = attendeeDetailCacheRef.current.get(attendeeId);
    const cachedAttendance = attendanceCacheRef.current.get(attendeeId);
    setSelectedAttendeeLoading(true);
    setLoadedAttendeeId("");

    Promise.all([
      cachedAttendee
        ? Promise.resolve({ data: cachedAttendee })
        : api.get(`/attendee-management/attendees/${attendeeId}`),
      cachedAttendance
        ? Promise.resolve({ data: cachedAttendance })
        : api
            .get("/attendee-management/attendance", { params: { attendeeId } })
            .catch(() => ({ data: [] })),
    ])
      .then(([attendeeResponse, attendanceResponse]) => {
        if (cancelled) return;
        const detailedAttendee = attendeeResponse.data || {};
        const attendeeAttendance = Array.isArray(attendanceResponse.data)
          ? attendanceResponse.data
          : [];
        attendeeDetailCacheRef.current.set(attendeeId, detailedAttendee);
        attendanceCacheRef.current.set(attendeeId, attendeeAttendance);
        setAttendees((current) =>
          current.map((attendee) =>
            Number(attendee.id) === attendeeId
              ? { ...attendee, ...detailedAttendee, _compact: false }
              : attendee,
          ),
        );
        setAttendance(attendeeAttendance);
        setLoadedAttendeeId(String(attendeeId));
      })
      .catch((loadError) => {
        if (cancelled) return;
        setAttendance([]);
        setError(
          messageFrom(
            loadError,
            "Não foi possível carregar os dados do atendido.",
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setSelectedAttendeeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loading, open, selectedAttendeeId]);

  const selectedAttendee = attendees.find(
    (attendee) => String(attendee.id) === String(selectedAttendeeId),
  );
  const selectedTemplate = templates.find(
    (template) => String(template.id) === String(selectedTemplateId),
  );
  const selectedTemplates = templates.filter(
    (template) => template.active !== false && selectedTemplateIds.includes(String(template.id)),
  );
  const visibleTemplates = templates.filter((template) => template.active !== false && (!normalizeText(templateQuery) || normalizeText(template.name).includes(normalizeText(templateQuery))));
  const toggleTemplate = (templateId, checked) => {
    const id = String(templateId);
    setSelectedTemplateIds((current) => {
      const next = checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id);
      setSelectedTemplateId(next[0] || "");
      return next;
    });
  };
  const variableBundle = useMemo(
    () =>
      buildAttendeeDocumentVariables({
        attendee: selectedAttendee || {},
        classes,
        groups,
        attendance,
        customAttendeeTabs,
      }),
    [selectedAttendee, classes, groups, attendance, customAttendeeTabs],
  );

  const flash = (setter, message) => {
    setter(message);
    window.setTimeout(() => setter(""), 3500);
  };

  const saveTemplate = async (draft = editing) => {
    const payload = {
      ...draft,
      content: cleanTemplateHtml(draft?.content),
    };
    delete payload._sourceRevision;
    const exactPdf = isExactPdfTemplate(payload);
    if (!payload.name?.trim() || !payload.title?.trim() || (!exactPdf && !payload.content?.trim())) {
      setError(exactPdf ? "Preencha nome e título do modelo." : "Preencha nome, título e conteúdo do modelo.");
      return;
    }
    setSaving(true);
    setError("");
    const previousIds = templates.map((item) => String(item.id));
    try {
      const response = templateHasId(payload.id)
        ? await api.put(`/attendee-management/document-templates/${payload.id}`, payload)
        : await api.post("/attendee-management/document-templates", payload);

      // O sucesso HTTP confirma a gravação; não exigir que o corpo do PUT/POST
      // contenha um ID. No PUT já conhecemos o identificador pelo próprio modelo.
      const returned = templateFromSaveResponse(response);
      const knownId = templateHasId(payload.id) ? payload.id : returned?.id;
      const revision = (Number(draft?._sourceRevision) || 0) + 1;
      const savedDraft = normalizeEnrollmentDeclaration({
        ...payload,
        ...(returned || {}),
        id: templateHasId(knownId) ? knownId : payload.id,
      });

      // Atualização local imediata enquanto recarregamos somente os modelos.
      if (templateHasId(knownId)) {
        setTemplates((current) => {
          const remaining = current.filter((item) => String(item.id) !== String(knownId));
          return [...remaining, savedDraft].sort(
            (left, right) => Number(left.sortOrder || 0) - Number(right.sortOrder || 0)
              || String(left.name || "").localeCompare(String(right.name || ""), "pt-BR"),
          );
        });
        setSelectedTemplateId(String(knownId));
      }
      setEditing({ ...savedDraft, _sourceRevision: revision });

      try {
        const refreshed = await api.get("/attendee-management/document-templates", {
          params: { _refresh: Date.now() },
        });
        if (!Array.isArray(refreshed.data?.templates)) {
          throw new Error("O servidor retornou uma lista de modelos inválida.");
        }
        const nextTemplates = refreshed.data.templates.map(normalizeEnrollmentDeclaration);
        const confirmed = findSavedTemplateInList({
          templates: nextTemplates,
          id: knownId,
          draft: savedDraft,
          previousIds,
        });
        if (!confirmed) {
          throw new Error("O modelo gravado ainda não apareceu na listagem atualizada.");
        }
        setTemplates(nextTemplates);
        if (typeof refreshed.data.canManage === "boolean") {
          setCanManage(refreshed.data.canManage);
        }
        setSelectedTemplateId(String(confirmed.id));
        setEditing({ ...confirmed, _sourceRevision: revision + 1 });
        flash(setSuccess, "Modelo salvo e documentos atualizados.");
      } catch (refreshError) {
        // Não transformar uma gravação HTTP bem-sucedida em erro de salvamento.
        // Para um novo modelo sem ID, evitar nova tentativa de POST às cegas.
        const warning = templateHasId(knownId)
          ? "Modelo salvo, mas não foi possível confirmar a atualização da lista. Reabra os modelos para sincronizar."
          : "O servidor aceitou a gravação, mas não confirmou o identificador do novo modelo. Reabra os modelos antes de salvar novamente.";
        flash(setSuccess, warning);
      }
    } catch (saveError) {
      setError(messageFrom(saveError, "Não foi possível salvar o modelo."));
    } finally {
      setSaving(false);
    }
  };

  const removeTemplate = async (template) => {
    if (!window.confirm(`Excluir o modelo “${template.name}”?`)) return;
    setError("");
    try {
      await api.delete(`/attendee-management/document-templates/${template.id}`);
      await load();
      flash(setSuccess, "Modelo excluído.");
    } catch (removeError) {
      setError(messageFrom(removeError, "Não foi possível excluir o modelo."));
    }
  };

  const generate = async () => {
    if (selectedAttendeeLoading || String(loadedAttendeeId) !== String(selectedAttendeeId)) {
      setError("Aguarde o carregamento dos dados do atendido.");
      return;
    }
    const templatesToGenerate = selectedTemplates.length ? selectedTemplates : (selectedTemplate ? [selectedTemplate] : []);
    if (!selectedAttendee || !templatesToGenerate.length) {
      setError("Selecione um atendido e ao menos um modelo de documento.");
      return;
    }
    const openedPdfWindow = window.open("", "_blank");
    if (!openedPdfWindow) {
      setError("O navegador bloqueou a janela do PDF. Autorize popups para este site.");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const exactTemplates = templatesToGenerate.filter((template) => isExactPdfTemplate(template));
      const htmlTemplates = templatesToGenerate.filter((template) => !isExactPdfTemplate(template));
      let exactPreviewUsed = false;
      for (const template of exactTemplates) {
        const response = await api.post(
          exactRenderEndpoint("attendee", template.id),
          { values: variableBundle.values },
          { responseType: "blob" },
        );
        const pdfUrl = URL.createObjectURL(response.data);
        if (!htmlTemplates.length && !exactPreviewUsed) {
          openedPdfWindow.location.href = pdfUrl;
          exactPreviewUsed = true;
        }
        const download = document.createElement("a");
        download.href = pdfUrl;
        download.download = safeFileNamePart(template.name, "documento") + "-" + safeFileNamePart(selectedAttendee.fullName, "atendido") + ".pdf";
        document.body.appendChild(download);
        download.click();
        download.remove();
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      }
      if (htmlTemplates.length) {
        const documents = htmlTemplates.map((template) => ({
          field: {
            id: safeFileNamePart(template.name, "documento"),
            label: template.name,
            documentTemplate: { title: template.title, content: cleanTemplateHtml(template.content) },
          },
          fields: variableBundle.fields,
          values: variableBundle.values,
          instance: { id: safeFileNamePart(selectedAttendee.fullName, "atendido") },
        }));
        if (documents.length === 1) {
          await generateDocumentPdf({ ...documents[0], openedPdfWindow });
        } else {
          await generateDocumentPdfBatch({ documents, openedPdfWindow, fileName: "documentos-" + safeFileNamePart(selectedAttendee.fullName, "atendido") + ".pdf" });
        }
      } else if (!exactPreviewUsed) {
        openedPdfWindow.close();
      }
      const exactCount = exactTemplates.length;
      const htmlCount = htmlTemplates.length;
      flash(
        setSuccess,
        exactCount > 0
          ? exactCount + " documento(s) fiel(is) gerado(s) sobre o PDF original" + (htmlCount ? " e " + htmlCount + " documento(s) padrão gerado(s)." : ".")
          : (htmlCount > 1 ? htmlCount + " documentos gerados em um único PDF." : "Documento gerado com sucesso."),
      );
    } catch (generateError) {
      if (openedPdfWindow && !openedPdfWindow.closed) openedPdfWindow.close();
      setError(messageFrom(generateError, "Não foi possível gerar o documento."));
    } finally {
      setGenerating(false);
    }
  };

  if (!visible) return null;

  const navigationButton = navigationSlot
    ? createPortal(
        <button
          type="button"
          className={open ? "active" : ""}
          onClick={() => setOpen(true)}
        >
          <FaFileContract /> Documentos
        </button>,
        navigationSlot,
      )
    : null;

  return (
    <>
      {navigationButton}
      {open && (
        <div
          className="attendee-docs__backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Documentos personalizados dos atendidos"
        >
          <div className="attendee-docs__modal">
            <header>
              <div>
                <span>Gestão de Atendidos</span>
                <h2 className="attendee-docs__title"><FaFilePdf aria-hidden="true" /><span>Documentos personalizados</span><button type="button" className="attendee-docs__info" aria-label="Gere matrículas, contratos, declarações e autorizações com os dados do atendido."><FaInfoCircle aria-hidden="true" /><span role="tooltip">Gere matrículas, contratos, declarações e autorizações com os dados do atendido.</span></button></h2>
                
              </div>
              <div className="attendee-docs__header-actions">
                {tab === "templates" && editing && (
                  <button
                    type="button"
                    className="attendee-docs__back"
                    onClick={() => setEditing(null)}
                  >
                    <FaArrowLeft /> Voltar
                  </button>
                )}
                <button
                  type="button"
                  className="attendee-docs__close"
                  aria-label="Fechar documentos"
                  onClick={() => setOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>
            </header>
            <nav>
              <button
                className={tab === "generate" ? "active" : ""}
                onClick={() => {
                  setTab("generate");
                  setEditing(null);
                }}
              >
                Gerar documento
              </button>
              {canManage && (
                <button
                  className={tab === "templates" ? "active" : ""}
                  onClick={() => setTab("templates")}
                >
                  Configurar modelos
                </button>
              )}
            </nav>
            {error && <div className="attendee-docs__alert error">{error}</div>}
            {success && (
              <div className="attendee-docs__alert success">{success}</div>
            )}
            {loading ? (
              <div className="attendee-docs__loading">
                Carregando documentos e atendidos...
              </div>
            ) : (
              <main>
                {tab === "generate" && (
                  <div className="attendee-docs__generate attendee-docs__generate--single">
                    <section>
                      <label>
                        Buscar atendido
                        <input
                          value={attendeeQuery}
                          onChange={(event) => setAttendeeQuery(event.target.value)}
                          placeholder="Nome, matrícula ou CPF"
                        />
                      </label>
                      <label>
                        Status
                        <select
                          value={attendeeStatusFilter}
                          onChange={(event) => loadStatus(event.target.value)}
                        >
                          {ATTENDEE_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Atendido
                        <select
                          value={selectedAttendeeId}
                          onChange={(event) =>
                            setSelectedAttendeeId(event.target.value)
                          }
                        >
                          <option value="">Selecione</option>
                          {filteredAttendees.map((attendee) => (
                            <option key={attendee.id} value={attendee.id}>
                              {attendee.registrationNumber
                                ? `${attendee.registrationNumber} — `
                                : ""}
                              {attendee.fullName}
                            </option>
                          ))}
                        </select>
                      </label>
                      <fieldset className="attendee-docs__template-checklist">
                        <legend>Modelos <small>Selecione um ou mais para gerar em um único PDF</small></legend>
                        <div className="attendee-docs__template-toolbar">
                          <label><FaSearch /><input value={templateQuery} onChange={(event) => setTemplateQuery(event.target.value)} placeholder="Buscar modelo de documento" /></label>
                          <span><strong>{selectedTemplates.length}</strong> selecionado(s)</span>
                          <button type="button" onClick={() => setSelectedTemplateIds(visibleTemplates.map((template) => String(template.id)))}>Selecionar visíveis</button>
                          <button type="button" onClick={() => { setSelectedTemplateIds([]); setSelectedTemplateId(""); }}>Limpar</button>
                        </div>
                        <div className="attendee-docs__template-options">
                          {visibleTemplates.map((template) => (
                            <label key={template.id}>
                              <input type="checkbox" checked={selectedTemplateIds.includes(String(template.id))} onChange={(event) => toggleTemplate(template.id, event.target.checked)} />
                              <span><strong>{template.name}</strong><small>{selectedTemplateIds.includes(String(template.id)) ? 'Selecionado para o PDF' : 'Clique para incluir'}</small></span>
                            </label>
                          ))}
                          {!visibleTemplates.length && <p>Nenhum modelo encontrado.</p>}
                        </div>
                      </fieldset>
                      {selectedAttendee && (
                        <article className="attendee-docs__attendee-card">
                          <strong>{selectedAttendee.fullName}</strong>
                          <span>
                            Matrícula: {selectedAttendee.registrationNumber || "—"}
                          </span>
                          <span>CPF: {selectedAttendee.cpf || "—"}</span>
                          <span>
                            Status:{" "}
                            {ATTENDEE_STATUS_OPTIONS.find(
                              (option) =>
                                option.value === attendeeStatus(selectedAttendee),
                            )?.label || attendeeStatus(selectedAttendee)}
                          </span>
                        </article>
                      )}
                      {!filteredAttendees.length && (
                        <div className="attendee-docs__empty">
                          Nenhum atendido encontrado para os filtros informados.
                        </div>
                      )}
                      {!templates.length && (
                        <div className="attendee-docs__empty">
                          Nenhum modelo ativo cadastrado.
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => {
                                setTab("templates");
                                setEditing(defaultTemplate());
                              }}
                            >
                              Criar primeiro modelo
                            </button>
                          )}
                        </div>
                      )}
                      <button
                        className="attendee-docs__generate-button"
                        disabled={
                          generating ||
                          selectedAttendeeLoading ||
                          String(loadedAttendeeId) !== String(selectedAttendeeId) ||
                          !selectedAttendee ||
                          !selectedTemplates.length
                        }
                        onClick={generate}
                      >
                        <FaFilePdf />
                        {generating
                          ? "Gerando PDF..."
                          : selectedAttendeeLoading
                            ? "Carregando dados..."
                            : selectedTemplates.length > 1 ? `Gerar ${selectedTemplates.length} documentos em 1 PDF` : "Gerar documento em PDF"}
                      </button>
                    </section>
                  </div>
                )}
                {tab === "templates" && canManage && (
                  <div className="attendee-docs__templates">
                    {editing ? (
                      <TemplateEditor
                        value={editing}
                        onChange={setEditing}
                        onSave={saveTemplate}
                        saving={saving}
                        variables={variableBundle.fields}
                      />
                    ) : (
                      <>
                        <div className="attendee-docs__template-heading">
                          <div>
                            <h3>Modelos disponíveis</h3>
                            <p>
                              Os mesmos modelos podem ser usados para qualquer atendido.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditing(defaultTemplate())}
                          >
                            <FaPlus /> Novo modelo
                          </button>
                        </div>
                        <div className="attendee-docs__template-list">
                          {templates.map((template) => (
                            <article key={template.id}>
                              <div>
                                <strong>{template.name}</strong>
                                <span>{template.title}</span>
                                <small>
                                  {template.active === false ? "Inativo" : "Ativo"}
                                </small>
                              </div>
                              <div>
                                <button type="button" className="attendee-docs__icon-action" aria-label="Editar modelo" title="Editar" onClick={() => setEditing({ ...template })}><FaEdit aria-hidden="true" /><span>Editar</span></button>
                                <button type="button" className="danger attendee-docs__icon-action" aria-label="Excluir modelo" title="Excluir" onClick={() => removeTemplate(template)}><FaTrash aria-hidden="true" /><span>Excluir</span></button>
                              </div>
                            </article>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </main>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* Attendee-independent-documents-v1 */

/* attendee-docs-document-final-ux-v1 */

/* attendee-docs-contrast-refinement-v2 */

/* attendee-docs-batch-documents-20260915 */
